import Icon from '@nxsys/components/AppIconImage';
import {
  IconDashboard,
  IconAnalytics,
  IconCompanies,
  IconEmployee,
  IconAgency,
  IconProspects,
  IconPaymentSettings,
  IconTimesheets,
  IconInvoices,
  IconActivityLogs,
  IconPermissions,
} from '@nxsys/assets/icons';
import {
  TbClipboardList,
  TbCloudDownload,
  TbDatabaseImport,
  TbCoins,
  TbReceiptRefund,
  TbReportAnalytics,
  TbFileText,
  TbFileImport,
  TbFileExport,
  TbId,
  TbHistory,
  TbBell,
  TbSend,
  TbCalculator,
} from 'react-icons/tb';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { roles } from '@nxsys/store/constants';
import { composeUmbrellaIrelandRoutes } from './umbrellaIrelandRoutes';

const { admin, employee, employer, agency } = roles;

/**
 * Ireland-only sidebar items, module-scoped so `bureauIrelandRoutesConfig`
 * (below) and the umbrella×Ireland composition (bottom of this file) render
 * the identical object — extracted, not duplicated.
 */
const rpnMenuItem = {
  id: 'rpn',
  title: 'RPN Updates',
  messageId: 'sidebar.app.rpnUpdates',
  icon: <TbCloudDownload />,
  url: '/rpn',
  allowedRoles: [admin, employer],
  policies: ['CompanyRpnUpdates'],
};
const aepnMenuItem = {
  id: 'aepn',
  title: 'AEPN Updates',
  messageId: 'sidebar.app.aepnUpdates',
  icon: <TbBell />,
  url: '/aepn',
  allowedRoles: [admin, employer],
  policies: ['CompanyAepnUpdates'],
  companyLevelOnly: true,
};
const submitPsrMenuItem = {
  id: 'submit-psr',
  title: 'Submit PSR',
  messageId: 'sidebar.app.submitPSR',
  icon: <TbSend />,
  url: '/submit-psr',
  aliases: ['/revenue-filings/submit-psr'],
  allowedRoles: [admin, employer],
  policies: ['CompanySubmitPsr'],
  // PSR is filed per employer (PAYE), never for a subset of departments.
  companyLevelOnly: true,
};
const revenueFilingsHistoryMenuItem = {
  id: 'revenue-filings-history',
  title: 'Revenue Filings History',
  messageId: 'sidebar.app.RevenueFilingsHistory',
  type: 'collapse',
  icon: <TbHistory />,
  allowedRoles: [admin, employer],
  policies: ['CompanyRtiFilings'],
  // Employer-level filings, like the UK RTI filings menu.
  companyLevelOnly: true,
  children: [
    // NX-6140: one row per submission to Revenue. Listed first because it is
    // the view that matches how filings are actually sent now - a week's
    // payslips travel in one message, not one message each.
    {
      id: 'revenue-filings/psr-submissions',
      title: 'PSR Submissions',
      messageId: 'sidebar.app.RevenueFilingsHistory.PSRSubmissions',
      url: '/revenue-filings/psr-submissions',
      allowedRoles: [admin, employer],
      // Same gate as the per-employee view below: this is the same filings
      // data, grouped by submission rather than by worker. A permissionNames
      // entry was used here before and silently hid the item - the name had no
      // match in the policy catalogue, and an unmatched name filters the menu
      // row out rather than failing loudly.
      policies: ['CompanyRtiFilings'],
      companyLevelOnly: true,
    },
  ],
};

const bureauRoutesConfig = [
  {
    id: 'dashboards',
    title: 'Dashboard',
    messageId: 'sidebar.app.dashboard',
    icon: <Icon src={IconDashboard} alt="Dashboard" />,
    url: '/dashboards',
    allowedRoles: [admin, employer, employee],
    policies: ['CompanyDashboard'],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    messageId: 'sidebar.app.analytics',
    icon: <Icon src={IconAnalytics} alt="Analytics" />,
    url: '/analytics',
    allowedRoles: [admin, employer],
    policies: ['CompanyAnalytics'],
  },
  {
    id: 'companies',
    title: 'Employers',
    messageId: 'common.bureau.companies',
    icon: <Icon src={IconCompanies} alt="Companies" />,
    url: '/companies',
    allowedRoles: [admin, employer],
    policies: ['CompanyCompanies'],
  },
  {
    id: 'general',
    title: 'Profile',
    messageId: 'sidebar.app.profile',
    icon: <Icon src={IconEmployee} alt="Profile" />,
    url: '/general',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'payslip',
    title: 'Payslip',
    messageId: 'sidebar.app.payslips',
    icon: <Icon src={IconInvoices} alt="Payslip" />,
    url: '/payslip',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'earning-statement',
    title: 'CIS Statement of Earnings',
    messageId: 'sidebar.app.cisStatementOfEarnings',
    icon: <Icon src={IconInvoices} alt="CIS Statement" />,
    url: '/earning-statement',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'payroll',
    title: 'Payroll',
    messageId: 'sidebar.app.payroll',
    icon: <Icon src={IconInvoices} alt="Payroll" />,
    url: '/payroll',
    allowedRoles: [employer],
    policies: ['CompanyTimesheets'],
  },
  { type: 'divider' } as any,
  {
    id: 'employees-subcontractors',
    title: 'Employees',
    messageId: 'sidebar.app.employeeSubcontractors',
    icon: <Icon src={IconEmployee} alt="Employees" />,
    url: '/employees-subcontractors',
    aliases: ['/employees'],
    allowedRoles: [admin, employer],
    policies: ['CompanyEmployees'],
  },
  {
    id: 'prospects',
    title: 'Prospects',
    messageId: 'sidebar.app.prospects',
    icon: <Icon src={IconProspects} alt="Prospects" />,
    url: '/prospects',
    allowedRoles: [admin, employer],
    policies: ['CompanyProspects'],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'hmrc-update',
    title: 'HMRC Update',
    messageId: 'sidebar.app.hmrcUpdate',
    type: 'collapse',
    icon: <TbCloudDownload />,
    allowedRoles: [admin, employer],
    policies: ['CompanyHmrcUpdates'],
    companyLevelOnly: true,
    children: [
      {
        id: 'hmrc-update/p6-p9',
        title: 'P6/P9 Update',
        messageId: 'sidebar.app.hmrcUpdate.p6p9',
        url: 'hmrc-update/p6-p9',
        allowedRoles: [admin, employer],
        policies: ['CompanyHmrcUpdates'],
        companyLevelOnly: true,
      },
      {
        id: 'hmrc-update/student-loan',
        title: 'P6/P9 Update',
        messageId: 'sidebar.app.hmrcUpdate.studentLoan',
        url: 'hmrc-update/student-loan',
        allowedRoles: [admin, employer],
        policies: ['CompanyHmrcUpdates'],
        companyLevelOnly: true,
      },
    ],
  },
  {
    id: 'bulk-update',
    title: 'Bulk Update',
    messageId: 'sidebar.app.bulkUpdate',
    icon: <TbDatabaseImport />,
    url: '/bulk-update',
    allowedRoles: [admin, employer],
    policies: ['CompanyBulkUpdate'],
  },
  {
    id: 'statutory-payments',
    title: 'Statutory Payments',
    messageId: 'sidebar.app.statutory',
    type: 'collapse',
    icon: <TbCoins />,
    allowedRoles: [admin, employer],
    policies: ['CompanyStatutoryPayments'],
    children: [
      {
        id: 'statutory-leave',
        title: 'Leave',
        messageId: 'sidebar.app.statutory.leave',
        url: '/statutory-leave',
        allowedRoles: [admin, employer],
        policies: ['CompanyStatutoryPayments'],
      },
      {
        id: 'statutory-payment',
        title: 'Payments',
        messageId: 'sidebar.app.statutory.payments',
        url: '/statutory-payment',
        allowedRoles: [admin, employer],
        policies: ['CompanyStatutoryPayments'],
      },
    ],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'payments-settings',
    title: 'Payments Settings',
    messageId: 'sidebar.app.paymentsSettings',
    type: 'collapse',
    icon: <Icon src={IconPaymentSettings} alt="Payments Settings" />,
    allowedRoles: [admin, employer],
    policies: ['CompanyPaymentSettings'],
    children: [
      {
        id: 'payments',
        title: 'Payments',
        messageId: 'sidebar.app.paymentsSettings.payments',
        url: '/payments',
        allowedRoles: [admin, employer],
        policies: ['CompanyPaymentSettings'],
      },
      {
        id: 'deductions',
        title: 'Deductions',
        messageId: 'sidebar.app.paymentsSettings.deductions',
        url: '/deductions',
        allowedRoles: [admin, employer],
        policies: ['CompanyPaymentSettings'],
      },
      {
        id: 'payment-rates',
        title: 'Payment Rates',
        messageId: 'sidebar.app.paymentsSettings.paymentRates',
        url: '/payment-rates',
        allowedRoles: [admin, employer],
        policies: ['CompanyPaymentSettings'],
      },
      {
        id: 'vat-codes',
        title: 'VAT Codes',
        messageId: 'sidebar.app.paymentsSettings.vatCodes',
        url: '/vat-codes',
        allowedRoles: [admin, employer],
        policies: ['CompanyPaymentSettings'],
      },
    ],
  },
  {
    id: 'timesheets',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    type: 'collapse',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    allowedRoles: [admin, employer],
    policies: ['CompanyTimesheets'],
    children: [
      {
        id: 'timesheets/all',
        title: 'All Timesheets',
        messageId: 'sidebar.app.timesheets.all',
        url: '/timesheets',
        allowedRoles: [admin, employer],
        policies: ['CompanyTimesheets'],
      },
      {
        id: 'timesheets/pending',
        title: 'Pending Agency Approval',
        messageId: 'sidebar.app.timesheets.pendingAgencyApproval',
        url: '/timesheets/pending',
        allowedRoles: [admin, employer],
        policies: ['CompanyTimesheets'],
      },
      {
        id: 'timesheets/generate-draft-payroll',
        title: 'Generate Draft Payroll',
        messageId: 'sidebar.app.timesheets.generateDraftPayroll',
        url: '/timesheets/generate-draft-payroll',
        allowedRoles: [admin, employer],
        policies: ['CompanyTimesheets'],
      },
      {
        id: 'timesheets/awaiting-invoice-generation',
        title: 'Awaiting Invoice Generation',
        messageId: 'sidebar.app.timesheets.awaitingInvoiceGeneration',
        url: '/timesheets/awaiting-invoice-generation',
        allowedRoles: [admin, employer],
        policies: ['CompanyTimesheets'],
      },
    ],
  },
  {
    id: 'invoices',
    title: 'Invoices',
    messageId: 'sidebar.app.invoices.all',
    icon: <Icon src={IconInvoices} alt="Invoices" />,
    url: '/invoices',
    allowedRoles: [admin, employer],
    policies: ['CompanyInvoices', 'AgencyInvoices'],
  },
  {
    id: 'invoices/credit-notes',
    title: 'Credit Notes',
    messageId: 'sidebar.app.invoices.creditNotes',
    icon: <TbReceiptRefund />,
    url: '/invoices/credit-notes',
    aliases: ['/invoices/create-invoice-credit', '/invoices/credit-note'],
    allowedRoles: [admin, employer],
    policies: ['CompanyCreditNotes', 'AgencyCreditNotes'],
  },
  {
    id: 'timesheets-employee',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    url: '/timesheets-employee',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'reports',
    title: 'Reports',
    messageId: 'sidebar.app.reports',
    type: 'collapse',
    icon: <TbReportAnalytics />,
    url: '/reports',
    allowedRoles: [admin, employer],
    policies: ['CompanyReports'],
  },
  {
    id: 'accounting',
    title: 'Accounting',
    messageId: 'sidebar.app.accounting',
    type: 'collapse',
    icon: <TbCalculator />,
    url: '/accounting',
    allowedRoles: [admin, employer],
    // TODO NX-5235: dedicated ACCOUNTING permission to come; reuse Reports for now
    policies: ['CompanyReports'],
    companyLevelOnly: true,
    children: [
      {
        id: 'accounting/xero',
        title: 'Xero',
        messageId: 'sidebar.app.accounting.xero',
        url: '/accounting/xero',
        allowedRoles: [admin, employer],
        // TODO NX-5235: dedicated ACCOUNTING permission to come; reuse Reports for now
        policies: ['CompanyReports'],
        companyLevelOnly: true,
      },
    ],
  },
  {
    id: 'documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/documents',
    allowedRoles: [admin, employer],
    policies: ['CompanyDocuments'],
  },
  {
    id: 'import',
    title: 'Import',
    messageId: 'sidebar.app.import',
    type: 'collapse',
    icon: <TbFileImport />,
    allowedRoles: [admin, employer],
    policies: ['CompanyImport'],
    children: [
      {
        id: 'import/agencies',
        title: 'Agencies',
        messageId: 'sidebar.app.import.agencies',
        url: '/import/agencies',
        allowedRoles: [admin, employer],
        policies: ['CompanyImport'],
      },
      {
        id: 'import/employees-subcontractors',
        title: 'Employees',
        messageId: 'sidebar.app.import.employeesSubcontractors',
        url: '/import/employees-subcontractors',
        allowedRoles: [admin, employer],
        policies: ['CompanyImport'],
      },
      {
        id: 'import/prospects',
        title: 'Prospects',
        messageId: 'sidebar.app.import.prospects',
        url: '/import/prospects',
        allowedRoles: [admin, employer],
        policies: ['CompanyImport'],
      },
      {
        id: 'import/timesheets',
        title: 'Timesheets',
        messageId: 'sidebar.app.import.timesheets',
        url: '/import/timesheets',
        allowedRoles: [admin, employer],
        policies: ['CompanyImport'],
      },
    ],
  },
  {
    id: 'export',
    title: 'Export',
    messageId: 'sidebar.app.export',
    type: 'collapse',
    icon: <TbFileExport />,
    url: '/export',
    allowedRoles: [admin, employer],
    policies: ['CompanyExport'],
    // /export-company is company-wide: refused to a login limited to
    // departments in every company, so hide the entry for them.
    companyLevelOnly: true,
  },
  {
    id: 'KID',
    title: 'KID',
    messageId: 'sidebar.app.KID',
    type: 'collapse',
    icon: <TbId />,
    url: '/KID',
    allowedRoles: [admin, employer],
    policies: ['CompanyKid'],
  },
  {
    id: 'rti-filings-history',
    title: 'RTI Filings History',
    messageId: 'sidebar.app.RTIFilingsHistory',
    type: 'collapse',
    icon: <TbHistory />,
    aliases: ['/rti-filings'],
    allowedRoles: [admin, employer],
    policies: ['CompanyRtiFilings'],
    companyLevelOnly: true,
    children: [
      {
        id: 'rti-filings/fps',
        title: 'FPS',
        messageId: 'sidebar.app.RTIFilingsHistory.FPS',
        url: '/rti-filings/fps',
        allowedRoles: [admin, employer],
        policies: ['CompanyRtiFilings'],
        companyLevelOnly: true,
      },
      {
        id: 'rti-filings/eps',
        title: 'EPS',
        messageId: 'sidebar.app.RTIFilingsHistory.EPS',
        url: '/rti-filings/eps',
        allowedRoles: [admin, employer],
        policies: ['CompanyRtiFilings'],
        companyLevelOnly: true,
      },
    ],
  },
  {
    id: 'employee-documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/employee-documents',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'activity-log',
    title: 'Activity Logs',
    messageId: 'sidebar.app.activityLog',
    type: 'collapse',
    icon: <Icon src={IconActivityLogs} alt="Activity Logs" />,
    url: '/activity-log',
    allowedRoles: [admin, employer],
    policies: ['CompanyActivityLog'],
    actions: ['activityLog:list'],
  },
  {
    id: 'permissions',
    title: 'Permissions',
    messageId: 'sidebar.app.permissions',
    type: 'collapse',
    icon: <Icon src={IconPermissions} alt="Permissions" />,
    allowedRoles: [admin, employer],
    policies: ['CompanyPermissions'],
    actions: ['iam:listGroups', 'iam:listUsers', 'iam:assignWorkerPermissions'],
    children: [
      {
        id: 'permissions/company',
        title: 'employer',
        messageId: 'sidebar.app.permissions.company',
        url: '/permissions/company',
        allowedRoles: [admin, employer],
        policies: ['CompanyPermissions'],
        actions: ['iam:listGroups', 'iam:listUsers'],
      },
      {
        id: 'permissions/employees',
        title: 'Employees',
        messageId: 'sidebar.app.permissions.employees',
        url: '/permissions/employees',
        allowedRoles: [admin, employer],
        policies: ['CompanyPermissions'],
        actions: ['iam:assignWorkerPermissions'],
      },
    ],
  },
];

const bureauIrelandRoutesConfig = [
  {
    id: 'dashboards',
    title: 'Dashboard',
    messageId: 'sidebar.app.dashboard',
    icon: <Icon src={IconDashboard} alt="Dashboard" />,
    url: '/dashboards',
    allowedRoles: [admin, employer, employee],
    policies: ['CompanyDashboard'],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    messageId: 'sidebar.app.analytics',
    icon: <Icon src={IconAnalytics} alt="Analytics" />,
    url: '/analytics',
    allowedRoles: [admin, employer],
    policies: ['CompanyAnalytics'],
  },
  {
    id: 'companies',
    title: 'Employers',
    messageId: 'common.bureau.companies',
    icon: <Icon src={IconCompanies} alt="Companies" />,
    url: '/companies',
    allowedRoles: [admin, employer],
    policies: ['CompanyCompanies'],
  },
  {
    id: 'general',
    title: 'Profile',
    messageId: 'sidebar.app.profile',
    icon: <Icon src={IconEmployee} alt="Profile" />,
    url: '/general',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'payslip',
    title: 'Payslip',
    messageId: 'sidebar.app.payslips',
    icon: <Icon src={IconInvoices} alt="Payslip" />,
    url: '/payslip',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'earning-statement',
    title: 'CIS Statement of Earnings',
    messageId: 'sidebar.app.cisStatementOfEarnings',
    icon: <Icon src={IconInvoices} alt="CIS Statement" />,
    url: '/earning-statement',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'payroll',
    title: 'Payroll',
    messageId: 'sidebar.app.payroll',
    icon: <Icon src={IconInvoices} alt="Payroll" />,
    url: '/payroll',
    allowedRoles: [employer],
    policies: ['CompanyTimesheets'],
  },
  { type: 'divider' } as any,
  {
    id: 'employees-subcontractors',
    title: 'Employees',
    messageId: 'sidebar.app.employeeSubcontractors',
    icon: <Icon src={IconEmployee} alt="Employees" />,
    url: '/employees-subcontractors',
    aliases: ['/employees'],
    allowedRoles: [admin, employer],
    policies: ['CompanyEmployees'],
  },
  {
    id: 'prospects',
    title: 'Prospects',
    messageId: 'sidebar.app.prospects',
    icon: <Icon src={IconProspects} alt="Prospects" />,
    url: '/prospects',
    allowedRoles: [admin, employer],
    policies: ['CompanyProspects'],
  },
  { type: 'divider' } as { type: string },
  rpnMenuItem,
  aepnMenuItem,
  submitPsrMenuItem,
  {
    id: 'bulk-update',
    title: 'Bulk Update',
    messageId: 'sidebar.app.bulkUpdate',
    icon: <TbDatabaseImport />,
    url: '/bulk-update',
    allowedRoles: [admin, employer],
    policies: ['CompanyBulkUpdate'],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'payments-settings',
    title: 'Payments Settings',
    messageId: 'sidebar.app.paymentsSettings',
    type: 'collapse',
    icon: <Icon src={IconPaymentSettings} alt="Payments Settings" />,
    allowedRoles: [admin, employer],
    policies: ['CompanyPaymentSettings'],
    children: [
      {
        id: 'payments',
        title: 'Payments',
        messageId: 'sidebar.app.paymentsSettings.payments',
        url: '/payments',
        allowedRoles: [admin, employer],
        policies: ['CompanyPaymentSettings'],
      },
      {
        id: 'deductions',
        title: 'Deductions',
        messageId: 'sidebar.app.paymentsSettings.deductions',
        url: '/deductions',
        allowedRoles: [admin, employer],
        policies: ['CompanyPaymentSettings'],
      },
      {
        id: 'payment-rates',
        title: 'Payment Rates',
        messageId: 'sidebar.app.paymentsSettings.paymentRates',
        url: '/payment-rates',
        allowedRoles: [admin, employer],
        policies: ['CompanyPaymentSettings'],
      },
      {
        id: 'vat-codes',
        title: 'VAT Codes',
        messageId: 'sidebar.app.paymentsSettings.vatCodes',
        url: '/vat-codes',
        allowedRoles: [admin, employer],
        policies: ['CompanyPaymentSettings'],
      },
    ],
  },
  {
    id: 'timesheets',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    type: 'collapse',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    allowedRoles: [admin, employer],
    policies: ['CompanyTimesheets'],
    children: [
      {
        id: 'timesheets/all',
        title: 'All Timesheets',
        messageId: 'sidebar.app.timesheets.all',
        url: '/timesheets',
        allowedRoles: [admin, employer],
        policies: ['CompanyTimesheets'],
      },
      {
        id: 'timesheets/pending',
        title: 'Pending Agency Approval',
        messageId: 'sidebar.app.timesheets.pendingAgencyApproval',
        url: '/timesheets/pending',
        allowedRoles: [admin, employer],
        policies: ['CompanyTimesheets'],
      },
      {
        id: 'timesheets/generate-draft-payroll',
        title: 'Generate Draft Payroll',
        messageId: 'sidebar.app.timesheets.generateDraftPayroll',
        url: '/timesheets/generate-draft-payroll',
        allowedRoles: [admin, employer],
        policies: ['CompanyTimesheets'],
      },
    ],
  },
  {
    id: 'invoices',
    title: 'Invoices',
    messageId: 'sidebar.app.invoices.all',
    icon: <Icon src={IconInvoices} alt="Invoices" />,
    url: '/invoices',
    allowedRoles: [admin, employer],
    policies: ['CompanyInvoices', 'AgencyInvoices'],
  },
  {
    id: 'invoices/credit-notes',
    title: 'Credit Notes',
    messageId: 'sidebar.app.invoices.creditNotes',
    icon: <TbReceiptRefund />,
    url: '/invoices/credit-notes',
    aliases: ['/invoices/create-invoice-credit', '/invoices/credit-note'],
    allowedRoles: [admin, employer],
    policies: ['CompanyCreditNotes', 'AgencyCreditNotes'],
  },
  {
    id: 'timesheets-employee',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets.timesheetsEmployee',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    url: '/timesheets-employee',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'reports',
    title: 'Reports',
    messageId: 'sidebar.app.reports',
    type: 'collapse',
    icon: <TbReportAnalytics />,
    url: '/reports',
    allowedRoles: [admin, employer],
    policies: ['CompanyReports'],
  },
  {
    id: 'documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    type: 'collapse',
    icon: <TbFileText />,
    url: '/documents',
    allowedRoles: [admin, employer],
    policies: ['CompanyDocuments'],
  },
  {
    id: 'import',
    title: 'Import',
    messageId: 'sidebar.app.import',
    type: 'collapse',
    icon: <TbFileImport />,
    allowedRoles: [admin, employer],
    policies: ['CompanyImport'],
    children: [
      {
        id: 'import/agencies',
        title: 'Agencies',
        messageId: 'sidebar.app.import.agencies',
        url: '/import/agencies',
        allowedRoles: [admin, employer],
        policies: ['CompanyImport'],
      },
      {
        id: 'import/employees-subcontractors',
        title: 'Employees',
        messageId: 'sidebar.app.import.employeesSubcontractors',
        url: '/import/employees-subcontractors',
        allowedRoles: [admin, employer],
        policies: ['CompanyImport'],
      },
      {
        id: 'import/prospects',
        title: 'Prospects',
        messageId: 'sidebar.app.import.prospects',
        url: '/import/prospects',
        allowedRoles: [admin, employer],
        policies: ['CompanyImport'],
      },
      {
        id: 'import/timesheets',
        title: 'Timesheets',
        messageId: 'sidebar.app.import.timesheets',
        url: '/import/timesheets',
        allowedRoles: [admin, employer],
        policies: ['CompanyImport'],
      },
    ],
  },
  {
    id: 'export',
    title: 'Export',
    messageId: 'sidebar.app.export',
    type: 'collapse',
    icon: <TbFileExport />,
    url: '/export',
    allowedRoles: [admin, employer],
    policies: ['CompanyExport'],
    // /export-company is company-wide: refused to a login limited to
    // departments in every company, so hide the entry for them.
    companyLevelOnly: true,
  },
  // {
  //   id: 'KID',
  //   title: 'KID',
  //   messageId: 'sidebar.app.KID',
  //   type: 'collapse',
  //   icon: <TbId />,
  //   url: '/KID',
  //   allowedRoles: [admin, employer],
  //   policies: ['CompanyKid'],
  // },
  revenueFilingsHistoryMenuItem,
  {
    id: 'employee-documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/employee-documents',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'activity-log',
    title: 'Activity Logs',
    messageId: 'sidebar.app.activityLog',
    type: 'collapse',
    icon: <Icon src={IconActivityLogs} alt="Activity Logs" />,
    url: '/activity-log',
    allowedRoles: [admin, employer],
    policies: ['CompanyActivityLog'],
    actions: ['activityLog:list'],
  },
  {
    id: 'permissions',
    title: 'Permissions',
    messageId: 'sidebar.app.permissions',
    type: 'collapse',
    icon: <Icon src={IconPermissions} alt="Permissions" />,
    allowedRoles: [admin, employer],
    policies: ['CompanyPermissions'],
    actions: ['iam:listGroups', 'iam:listUsers', 'iam:assignWorkerPermissions'],
    children: [
      {
        id: 'permissions/company',
        title: 'employer',
        messageId: 'sidebar.app.permissions.company',
        url: '/permissions/company',
        allowedRoles: [admin, employer],
        policies: ['CompanyPermissions'],
        actions: ['iam:listGroups', 'iam:listUsers'],
      },
      {
        id: 'permissions/employees',
        title: 'Employees',
        messageId: 'sidebar.app.permissions.employees',
        url: '/permissions/employees',
        allowedRoles: [admin, employer],
        policies: ['CompanyPermissions'],
        actions: ['iam:assignWorkerPermissions'],
      },
    ],
  },
];

const umbrellaRoutesConfig = [
  {
    id: 'dashboards',
    title: 'Dashboard',
    messageId: 'sidebar.app.dashboard',
    icon: <Icon src={IconDashboard} alt="Dashboard" />,
    url: '/dashboards',
    allowedRoles: [admin, agency, employee],
    policies: ['CompanyDashboard', 'AgencyDashboard'],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    messageId: 'sidebar.app.analytics',
    icon: <Icon src={IconAnalytics} alt="Analytics" />,
    url: '/analytics',
    allowedRoles: [admin],
    policies: ['CompanyAnalytics'],
  },
  {
    id: 'companies',
    title: 'Companies',
    messageId: 'sidebar.app.companies',
    icon: <Icon src={IconCompanies} alt="Companies" />,
    url: '/companies',
    allowedRoles: [admin],
    policies: ['CompanyCompanies'],
  },
  {
    id: 'general',
    title: 'Profile',
    messageId: 'sidebar.app.profile',
    icon: <Icon src={IconEmployee} alt="Profile" />,
    url: '/general',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'payslip',
    title: 'Payslip',
    messageId: 'sidebar.app.payslips',
    icon: <Icon src={IconInvoices} alt="Payslip" />,
    url: '/payslip',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'earning-statement',
    title: 'CIS Statement of Earnings',
    messageId: 'sidebar.app.cisStatementOfEarnings',
    icon: <Icon src={IconInvoices} alt="CIS Statement" />,
    url: '/earning-statement',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'employees-subcontractors',
    title: 'Employees',
    messageId: 'sidebar.app.employeeSubcontractors',
    icon: <Icon src={IconEmployee} alt="Employees" />,
    url: '/employees-subcontractors',
    aliases: ['/employees'],
    allowedRoles: [admin, agency],
    policies: ['CompanyEmployees', 'AgencyEmployees'],
  },
  {
    id: 'agency',
    title: 'agency',
    messageId: 'sidebar.app.agency',
    icon: <Icon src={IconAgency} alt="Agency" />,
    url: '/agency',
    allowedRoles: [admin],
    policies: ['CompanyAgencies'],
  },
  {
    id: 'assignments',
    title: 'Assignments',
    messageId: 'sidebar.app.assignments',
    type: 'collapse',
    icon: <TbClipboardList />,
    url: '/assignments',
    allowedRoles: [admin, agency, employee],
    policies: ['CompanyAssignments', 'AgencyAssignments'],
  },
  {
    id: 'prospects',
    title: 'Prospects',
    messageId: 'sidebar.app.prospects',
    icon: <Icon src={IconProspects} alt="Prospects" />,
    url: '/prospects',
    allowedRoles: [admin, agency],
    policies: ['CompanyProspects', 'AgencyProspects'],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'hmrc-update',
    title: 'HMRC Update',
    messageId: 'sidebar.app.hmrcUpdate',
    type: 'collapse',
    icon: <TbCloudDownload />,
    allowedRoles: [admin],
    policies: ['CompanyHmrcUpdates'],
    companyLevelOnly: true,
    children: [
      {
        id: 'hmrc-update/p6-p9',
        title: 'P6/P9 Update',
        messageId: 'sidebar.app.hmrcUpdate.p6p9',
        url: 'hmrc-update/p6-p9',
        allowedRoles: [admin],
        policies: ['CompanyHmrcUpdates'],
        companyLevelOnly: true,
      },
      {
        id: 'hmrc-update/student-loan',
        title: 'P6/P9 Update',
        messageId: 'sidebar.app.hmrcUpdate.studentLoan',
        url: 'hmrc-update/student-loan',
        allowedRoles: [admin],
        policies: ['CompanyHmrcUpdates'],
        companyLevelOnly: true,
      },
    ],
  },
  {
    id: 'bulk-update',
    title: 'Bulk Update',
    messageId: 'sidebar.app.bulkUpdate',
    icon: <TbDatabaseImport />,
    url: '/bulk-update',
    allowedRoles: [admin],
    policies: ['CompanyBulkUpdate'],
  },
  {
    id: 'statutory-payments',
    title: 'Statutory Payments',
    messageId: 'sidebar.app.statutory',
    type: 'collapse',
    icon: <TbCoins />,
    allowedRoles: [admin, employer],
    policies: ['CompanyStatutoryPayments'],
    children: [
      {
        id: 'statutory-leave',
        title: 'Leave',
        messageId: 'sidebar.app.statutory.leave',
        url: '/statutory-leave',
        allowedRoles: [admin, employer],
        policies: ['CompanyStatutoryPayments'],
      },
      {
        id: 'statutory-payment',
        title: 'Payments',
        messageId: 'sidebar.app.statutory.payments',
        url: '/statutory-payment',
        allowedRoles: [admin, employer],
        policies: ['CompanyStatutoryPayments'],
      },
    ],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'payments-settings',
    title: 'Payments Settings',
    messageId: 'sidebar.app.paymentsSettings',
    type: 'collapse',
    icon: <Icon src={IconPaymentSettings} alt="Payments Settings" />,
    allowedRoles: [admin],
    policies: ['CompanyPaymentSettings'],
    children: [
      {
        id: 'payments',
        title: 'Payments',
        messageId: 'sidebar.app.paymentsSettings.payments',
        url: '/payments',
        allowedRoles: [admin],
        policies: ['CompanyPaymentSettings'],
      },
      {
        id: 'deductions',
        title: 'Deductions',
        messageId: 'sidebar.app.paymentsSettings.deductions',
        url: '/deductions',
        allowedRoles: [admin],
        policies: ['CompanyPaymentSettings'],
      },
      {
        id: 'payment-rates',
        title: 'Payment Rates',
        messageId: 'sidebar.app.paymentsSettings.paymentRates',
        url: '/payment-rates',
        allowedRoles: [admin],
        policies: ['CompanyPaymentSettings'],
      },
      {
        id: 'vat-codes',
        title: 'VAT Codes',
        messageId: 'sidebar.app.paymentsSettings.vatCodes',
        url: '/vat-codes',
        allowedRoles: [admin],
        policies: ['CompanyPaymentSettings'],
      },
    ],
  },
  {
    id: 'timesheets',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    type: 'collapse',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    allowedRoles: [admin],
    policies: ['CompanyTimesheets'],
    children: [
      {
        id: 'timesheets/all',
        title: 'All Timesheets',
        messageId: 'sidebar.app.timesheets.all',
        url: '/timesheets',
        allowedRoles: [admin],
        policies: ['CompanyTimesheets'],
      },
      {
        id: 'timesheets/pending',
        title: 'Pending Agency Approval',
        messageId: 'sidebar.app.timesheets.pendingAgencyApproval',
        url: '/timesheets/pending',
        allowedRoles: [admin],
        policies: ['CompanyTimesheets'],
      },
      {
        id: 'timesheets/generate-draft-payroll',
        title: 'Generate Draft Payroll',
        messageId: 'sidebar.app.timesheets.generateDraftPayroll',
        url: '/timesheets/generate-draft-payroll',
        allowedRoles: [admin],
        policies: ['CompanyTimesheets'],
      },
      {
        id: 'timesheets/awaiting-invoice-generation',
        title: 'Awaiting Invoice Generation',
        messageId: 'sidebar.app.timesheets.awaitingInvoiceGeneration',
        url: '/timesheets/awaiting-invoice-generation',
        allowedRoles: [admin],
        policies: ['CompanyTimesheets'],
      },
    ],
  },
  {
    id: 'invoices',
    title: 'Invoices',
    messageId: 'sidebar.app.invoices.all',
    icon: <Icon src={IconInvoices} alt="Invoices" />,
    url: '/invoices',
    allowedRoles: [admin, agency],
    policies: ['CompanyInvoices', 'AgencyInvoices'],
  },
  {
    id: 'invoices/credit-notes',
    title: 'Credit Notes',
    messageId: 'sidebar.app.invoices.creditNotes',
    icon: <TbReceiptRefund />,
    url: '/invoices/credit-notes',
    aliases: ['/invoices/create-invoice-credit', '/invoices/credit-note'],
    allowedRoles: [admin, agency],
    policies: ['CompanyCreditNotes', 'AgencyCreditNotes'],
  },
  {
    id: 'timesheets-agency',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    url: '/timesheets-agency',
    allowedRoles: [agency],
    policies: ['AgencyTimesheets'],
  },
  {
    id: 'timesheets-employee',
    title: 'Timesheets',
    messageId: 'sidebar.app.timesheets',
    icon: <Icon src={IconTimesheets} alt="Timesheets" />,
    url: '/timesheets-employee',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'reports',
    title: 'Reports',
    messageId: 'sidebar.app.reports',
    type: 'collapse',
    icon: <TbReportAnalytics />,
    url: '/reports',
    allowedRoles: [admin],
    policies: ['CompanyReports'],
  },
  {
    id: 'accounting',
    title: 'Accounting',
    messageId: 'sidebar.app.accounting',
    type: 'collapse',
    icon: <TbCalculator />,
    url: '/accounting',
    allowedRoles: [admin],
    // TODO NX-5235: dedicated ACCOUNTING permission to come; reuse Reports for now
    policies: ['CompanyReports'],
    companyLevelOnly: true,
    children: [
      {
        id: 'accounting/xero',
        title: 'Xero',
        messageId: 'sidebar.app.accounting.xero',
        url: '/accounting/xero',
        allowedRoles: [admin],
        // TODO NX-5235: dedicated ACCOUNTING permission to come; reuse Reports for now
        policies: ['CompanyReports'],
        companyLevelOnly: true,
      },
    ],
  },
  {
    id: 'documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/documents',
    allowedRoles: [admin],
    policies: ['CompanyDocuments'],
  },
  {
    id: 'import',
    title: 'Import',
    messageId: 'sidebar.app.import',
    type: 'collapse',
    icon: <TbFileImport />,
    allowedRoles: [admin],
    policies: ['CompanyImport'],
    children: [
      {
        id: 'import/agencies',
        title: 'Agencies',
        messageId: 'sidebar.app.import.agencies',
        url: '/import/agencies',
        allowedRoles: [admin],
        policies: ['CompanyImport'],
      },
      {
        id: 'import/employees-subcontractors',
        title: 'Employees',
        messageId: 'sidebar.app.import.employeesSubcontractors',
        url: '/import/employees-subcontractors',
        allowedRoles: [admin],
        policies: ['CompanyImport'],
      },
      {
        id: 'import/prospects',
        title: 'Prospects',
        messageId: 'sidebar.app.import.prospects',
        url: '/import/prospects',
        allowedRoles: [admin],
        policies: ['CompanyImport'],
      },
      {
        id: 'import/timesheets',
        title: 'Timesheets',
        messageId: 'sidebar.app.import.timesheets',
        url: '/import/timesheets',
        allowedRoles: [admin],
        policies: ['CompanyImport'],
      },
    ],
  },
  {
    id: 'export',
    title: 'Export',
    messageId: 'sidebar.app.export',
    type: 'collapse',
    icon: <TbFileExport />,
    url: '/export',
    allowedRoles: [admin, employer],
    policies: ['CompanyExport'],
    // /export-company is company-wide: refused to a login limited to
    // departments in every company, so hide the entry for them.
    companyLevelOnly: true,
  },
  {
    id: 'KID',
    title: 'KID',
    messageId: 'sidebar.app.KID',
    type: 'collapse',
    icon: <TbId />,
    url: '/KID',
    allowedRoles: [admin, agency],
    policies: ['CompanyKid', 'AgencyKid'],
  },
  {
    id: 'rti-filings-history',
    title: 'RTI Filings History',
    messageId: 'sidebar.app.RTIFilingsHistory',
    type: 'collapse',
    icon: <TbHistory />,
    aliases: ['/rti-filings'],
    allowedRoles: [admin],
    policies: ['CompanyRtiFilings'],
    companyLevelOnly: true,
    children: [
      {
        id: 'rti-filings/fps',
        title: 'FPS',
        messageId: 'sidebar.app.RTIFilingsHistory.FPS',
        url: '/rti-filings/fps',
        allowedRoles: [admin],
        policies: ['CompanyRtiFilings'],
        companyLevelOnly: true,
      },
      {
        id: 'rti-filings/eps',
        title: 'EPS',
        messageId: 'sidebar.app.RTIFilingsHistory.EPS',
        url: '/rti-filings/eps',
        allowedRoles: [admin],
        policies: ['CompanyRtiFilings'],
        companyLevelOnly: true,
      },
      {
        id: 'rti-filings/cis-300',
        title: 'CIS 300',
        messageId: 'sidebar.app.RTIFilingsHistory.CIS300',
        url: '/rti-filings/cis-300',
        allowedRoles: [admin],
        policies: ['CompanyRtiFilings'],
        companyLevelOnly: true,
      },
    ],
  },
  {
    id: 'agency-documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/agency-documents',
    allowedRoles: [agency],
    policies: ['AgencyDocuments'],
  },
  {
    id: 'employee-documents',
    title: 'Documents',
    messageId: 'sidebar.app.documents',
    icon: <TbFileText />,
    url: '/employee-documents',
    allowedRoles: [employee],
    noCheckPermissionWith: [employee],
  },
  {
    id: 'agency-reports',
    title: 'Reports',
    messageId: 'sidebar.app.reports',
    type: 'collapse',
    icon: <TbReportAnalytics />,
    allowedRoles: [agency],
    policies: ['AgencyAwr', 'AgencyWtr'],
    children: [
      {
        id: 'reports/agency/awr',
        title: 'AWR',
        messageId: 'common.awr',
        url: '/reports/agency/awr',
        allowedRoles: [agency],
        policies: ['AgencyAwr'],
      },
      {
        id: 'reports/agency/wtr',
        title: 'WTR',
        messageId: 'common.wtr',
        url: '/reports/agency/wtr',
        allowedRoles: [agency],
        policies: ['AgencyWtr'],
      },
    ],
  },
  {
    id: 'reports/agency/employment-intermediary',
    title: 'Employment Intermediary',
    messageId: 'common.employmentIntermediary',
    icon: <TbReportAnalytics />,
    url: '/reports/agency/employment-intermediary',
    allowedRoles: [agency],
    policies: ['AgencyEmploymentIntermediaryReport'],
  },
  { type: 'divider' } as { type: string },
  {
    id: 'activity-log',
    title: 'Activity Logs',
    messageId: 'sidebar.app.activityLog',
    type: 'collapse',
    icon: <Icon src={IconActivityLogs} alt="Activity Logs" />,
    url: '/activity-log',
    allowedRoles: [admin],
    policies: ['CompanyActivityLog'],
    actions: ['activityLog:list'],
  },
  {
    id: 'permissions',
    title: 'Permissions',
    messageId: 'sidebar.app.permissions',
    type: 'collapse',
    icon: <Icon src={IconPermissions} alt="Permissions" />,
    allowedRoles: [admin],
    policies: ['CompanyPermissions'],
    actions: [
      'iam:listGroups',
      'iam:listUsers',
      'iam:assignWorkerPermissions',
      'iam:assignAgencyPermissions',
    ],
    children: [
      {
        id: 'permissions/company',
        title: 'employer',
        messageId: 'sidebar.app.permissions.company',
        url: '/permissions/company',
        allowedRoles: [admin],
        policies: ['CompanyPermissions'],
        actions: ['iam:listGroups', 'iam:listUsers'],
      },
      {
        id: 'permissions/candidates',
        title: 'Candidates',
        messageId: 'sidebar.app.permissions.candidates',
        url: '/permissions/candidates',
        allowedRoles: [admin],
        policies: ['CompanyPermissions'],
        actions: ['iam:assignWorkerPermissions'],
      },
      {
        id: 'permissions/agency',
        title: 'agency',
        messageId: 'sidebar.app.permissions.agency',
        url: '/permissions/agency',
        allowedRoles: [admin],
        policies: ['CompanyPermissions'],
        actions: ['iam:assignAgencyPermissions'],
      },
    ],
  },
];

/**
 * `agency-reports` keeps running for umbrella-Ireland (48-hour WTR still
 * applies) but its AWR child has no Ireland equivalent (ticket: "No AWR").
 * Derived from the UK item so the WTR entry itself is byte-identical.
 */
const agencyReportsIrelandMenuItem = (() => {
  const ukItem = umbrellaRoutesConfig.find(
    (item: any) => item.id === 'agency-reports',
  ) as any;
  return {
    ...ukItem,
    policies: ['AgencyWtr'],
    children: ukItem.children.filter(
      (child: any) => child.id === 'reports/agency/wtr',
    ),
  };
})();

const umbrellaIrelandRoutesConfig = composeUmbrellaIrelandRoutes(
  umbrellaRoutesConfig as any,
  {
    'hmrc-update': [rpnMenuItem, aepnMenuItem, submitPsrMenuItem],
    'rti-filings-history': [revenueFilingsHistoryMenuItem],
    'agency-reports': [agencyReportsIrelandMenuItem],
  },
);

const routesConfig = (
  isBureau: boolean,
  payrollCountry: PayrollCountryType,
) => {
  if (payrollCountry === 'IRELAND') {
    return isBureau ? bureauIrelandRoutesConfig : umbrellaIrelandRoutesConfig;
  }
  return isBureau ? bureauRoutesConfig : umbrellaRoutesConfig;
};

export {
  bureauRoutesConfig,
  bureauIrelandRoutesConfig,
  umbrellaRoutesConfig,
  umbrellaIrelandRoutesConfig,
};
export default routesConfig;
